import { 
    Button,
    Box,
    Paper,
    Typography,
    Chip,
    Divider,
    Alert,
    LinearProgress,
    CircularProgress,
    IconButton,
    Tooltip
} from "@mui/material"
import { 
    Help as HelpIcon, 
    Book as DictionaryIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material'

import "./style.css"

import { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { userRequest } from "../requestMethods"

import { v4 as uuidv4 } from 'uuid'

import WordAddForm from "./WordAddForm"
import OneWordEditor from "./OneWordEditor"
import DictionaryEditorDialog from "./DictionaryEditorDialog"
import TranslationViewerDialog from "./TranslationViewerDialog"
import TranslationCreator from "./TranslationCreator"
import TranslationPhraseEditor from "./TranslationPhraseEditor"
import AITranslationDialog from "./AITranslationDialog"
import TranslationEditorGuide from "./UserGuides/TranslationEditorGuide"
import PageContainer from "../Components/layout/PageContainer"

function TranslationEditor(props) {

    const location = useLocation();
    const urlParams = location.pathname.split('/');
    const textId = urlParams[urlParams.length - 1]

    const navigate = useNavigate()
    const isMountedRef = useRef(true)

    const [text, setText] = useState(null)
    const [translation, setTranslation] = useState(null)
    const [error, setError] = useState(null)
    const [loadingText, setLoadingText] = useState(false)
    const [loadingTranslation, setLoadingTranslation] = useState(false)
    const [translationNotFound, setTranslationNotFound] = useState(false)
    
    const [wholeTextTranslation, setWholeTextTranslation] = useState(null)

    const [positionList, setPositionList] = useState([])

    const [mainDictionaryMap, setMainDictionaryMap] = useState(null)
    const [textWordObjects, setTextWordObjects] = useState([])

    // We use this to open a dictionary editor and scroll to specific word
    const [wordToOpenInDictionary, setWordToOpenInDictionary] = useState(null)

    // there might be a case when a text already has a dictionary on remote,
    // but its dictionary is saved in the browser as well. We use this
    // state variable to control that case. 
    const [isDictionaryLoaded, setDictionaryLoaded] = useState(false)

    const [phraseObjects, setPhraseObjects] = useState([])
    const [phrasesDictionary, setPhrasesDictionary] = useState(null)

    // AI Translation states
    const [aiTranslationDialogOpen, setAiTranslationDialogOpen] = useState(false)
    const [aiTranslationResponse, setAiTranslationResponse] = useState(null)
    const [aiTranslationLoading, setAiTranslationLoading] = useState(false)

    // User Guide state
    const [userGuideOpen, setUserGuideOpen] = useState(false)

    // Auto-translation state
    const [autoTranslationLoading, setAutoTranslationLoading] = useState(false)
    const [visibleWordRows, setVisibleWordRows] = useState(150)

    let wordObjectTemplate = {
        translation: '',
        dictionaryWordId: '',
        description : '',
        lemma: '',
        pos: '',
        tag: '',
        word: '',
        mapping_position: {
            left: 0,
            right: 0,
        },
        positions: {
            left: 0,
            right: 0,
        },
        grammar_topics: []
    }

    const wordJsonFormat = {
        id: -1,
        word: '', 
        globalID: '',
        translation: '', 
        comment: '',
        spacyPos: '',
        linkTitle: '', 
        link: ''  
      }

    const parseError = (err) => {
        let e = JSON.stringify(err)
        if (e === '{}')
            setError(err.toString())
        else if (err.response && err.response.data)
            setError(err.response.data)
        else
            setError(JSON.stringify(err))

    }

    const fetchTextAPI = async () => {
        if (!textId) {
            console.log('FetchText: TextId is not provided')
            return
        }
        
        try {
            if (isMountedRef.current) {
                setLoadingText(true)
            }
            let res = await userRequest.get('/text/' + textId)
            if (isMountedRef.current) {
                setText(res.data)
                setError(null)
            }
        } catch (err) {
            console.log('Fetch Text error: ' + err)
            if (isMountedRef.current) {
                parseError(err)
            }
        } finally {
            if (isMountedRef.current) {
                setLoadingText(false)
            }
        }
    }

    const fetchTranslationAPI = async () => {
        if (!textId) {
            console.log('FetchTranslation: TextId is not provided')
            return
        }

        try {
            if (isMountedRef.current) {
                setLoadingTranslation(true)
                setTranslationNotFound(false)
            }
            let res = await userRequest.get('/translation/ofText/' + textId)
            console.log(res.data)
            
            if (!isMountedRef.current) return;
            
            setTranslation(res.data)
            setTranslationNotFound(false)
            if (res.data.wholeText)
                setWholeTextTranslation(res.data.wholeText)

            if (res.data.dictionary) {
                let remoteDict = createDictionaryMapFromArray(res.data.dictionary)
                if (isDictionaryLoaded) {
                    let ok = window.confirm("Text's dictionary and words table were downloaded from server. Would you like to use it?")
                    if (ok) {
                        setMainDictionaryMap(remoteDict)
                        setDictionaryLoaded(true)
                    }
                }
                else {
                    setMainDictionaryMap(remoteDict)
                    setDictionaryLoaded(true)
                }
            }
            
            if (res.data.wordObjects) {
                let wordObjects = res.data.wordObjects
                let posList = []
                wordObjects.forEach(wordObj => {
                    posList.push(wordObj.positions)
                })
                
                setPositionList(posList)
                setTextWordObjects(wordObjects)
            }
            if (res.data.phraseObjects) {
                setPhraseObjects(res.data.phraseObjects)
            }
            if (res.data.phrasesDictionary) {
                setPhrasesDictionary(createDictionaryMapFromArray(res.data.phrasesDictionary))
            }

            setError(null)
        } catch (err) {
            console.log('Fetch Translation error: ' + err)
            if (isMountedRef.current) {
                if (err.response && err.response.status === 404) {
                    setTranslation(null)
                    setTranslationNotFound(true)
                    setError(null)
                } else {
                    parseError(err)
                }
            }
        } finally {
            if (isMountedRef.current) {
                setLoadingTranslation(false)
            }
        }
    }

    const createArrayFromDictionaryMap = (dictMap) => {
        let arr = []
        dictMap.forEach((value, key) => {
            arr.push({ ...value })
        })
        return arr
    }
    
    const createDictionaryMapFromArray = (arr) => {
        let dictMap = new Map()
        arr.forEach((dictWord) => {
            // Use id if available, otherwise use _id (for MongoDB documents)
            const key = dictWord.id || dictWord._id
            dictMap.set(key, dictWord)
        })
        return dictMap
    }

    const createTranslationAPI = async () => {
        try {
            let data = {
                text: text._id,
                textLanguage: text.language,
                translationLanguage: (translation && translation.length) > 0 ? translation.translationLanguage : "63161a72739fbae23dc2f6e2", //default: O'zbekcha
                wordObjects: textWordObjects,
                dictionary: createArrayFromDictionaryMap(mainDictionaryMap),
                wholeText: wholeTextTranslation ? wholeTextTranslation : ' ',
                phraseObjects: phraseObjects,
                phrasesDictionary: createArrayFromDictionaryMap(phrasesDictionary)

            }

            let res = await userRequest.post('/translation', data)
            setTranslation(res.data)
            alert('Created!')
            setError(null)
        } catch (err) {
            console.error('Create translation error:', err);
            const errorMessage = parseSaveError(err);
            alert('Failed to create translation: ' + errorMessage);
            parseError(err);
        }
    }

    const updateTranslationAPI = async () => {
        try {
            let data = {
                // text : text._id,
                // textLanguage : text.language,
                // translationLanguage : translation.length > 0 ? translation.translationLanguage : "63161a72739fbae23dc2f6e2",
                wordObjects: textWordObjects,
                dictionary: createArrayFromDictionaryMap(mainDictionaryMap),
                wholeText: wholeTextTranslation,
                phraseObjects: phraseObjects,
                phrasesDictionary: createArrayFromDictionaryMap(phrasesDictionary)
            }

            let res = await userRequest.patch('/translation/' + translation._id, data)
            setTranslation(res.data)
            alert('Saved!')
            setError(null)
        } catch (err) {
            console.error('Update translation error:', err);
            const errorMessage = parseSaveError(err);
            alert('Failed to save translation: ' + errorMessage);
            parseError(err);
        }
    }

    const validateDictionaryBeforeSave = () => {
        const invalidEntries = [];
        if (mainDictionaryMap) {
            mainDictionaryMap.forEach((entry, id) => {
                if (!entry.word?.trim()) {
                    invalidEntries.push(`Dictionary entry (id: ${id}): word is empty`);
                } else if (!entry.pos?.trim()) {
                    invalidEntries.push(`"${entry.word}": Part of speech (pos) is required`);
                }
            });
        }
        return invalidEntries;
    };

    const parseSaveError = (err) => {
        const data = err.response?.data;
        if (!data) return err.message || 'Unknown error';
        if (typeof data === 'string') return data;
        if (data.message) return data.message;
        if (data.errors && typeof data.errors === 'object') {
            const messages = Object.values(data.errors).map(e => 
                typeof e === 'object' && e?.message ? e.message : String(e)
            );
            return 'Validation failed: ' + messages.join('; ');
        }
        if (data.error) return data.error;
        return JSON.stringify(data);
    };

    const onSaveButtonClicked = async () => {
        const dictErrors = validateDictionaryBeforeSave();
        if (dictErrors.length > 0) {
            alert(
                'Cannot save: Some dictionary entries have missing required fields.\n\n' +
                dictErrors.slice(0, 5).join('\n') +
                (dictErrors.length > 5 ? `\n... and ${dictErrors.length - 5} more` : '') +
                '\n\nPlease fix these in the Dictionary editor (Word and Part of speech are required).'
            );
            return;
        }
        if (translation && translation._id)
            await updateTranslationAPI()
        else
            await createTranslationAPI()
    }
      
    const saveDictionaryToLocalStorage = () => {
        if (!text || !mainDictionaryMap)
            return
        // Convert the object to a JSON string
        const objectString = JSON.stringify({
            textId: text._id,
            dictionary: Array.from(mainDictionaryMap),
            wordObjects: textWordObjects
        });
        
        // Save the string to localStorage
        localStorage.setItem("dictionary", objectString);
    }

    const [addWordMode, setAddWordMode] = useState(false)
    const [editedWord, setEditedWord] = useState(null)
    const [editedWordIndex, setEditedWordIndex] = useState(-1)
 
    const onAddWordButtonClicked = (index) => {
        if (index >= textWordObjects.length) {
            console.log('word index is out of range')
            return
        }

        let newWord = { 
            ...wordJsonFormat, 
            id: 0, 
            word: textWordObjects[index].word.toLowerCase(),
            pos: textWordObjects[index].pos || ''
        }
        setEditedWord(newWord)
        setEditedWordIndex(index)
        setAddWordMode(true)
        // console.log(newWord)
    }

    const onAddWord = (newWord) => {
        // console.log(newWord)
        let id = uuidv4()
        if (mainDictionaryMap && mainDictionaryMap.has(id)) {
            alert("Couldn't make unique ID for new word, please try again.")
            return
        }

        let newDictionary
        if (mainDictionaryMap)
            newDictionary = new Map(mainDictionaryMap)
        else
            newDictionary = new Map()
        
        newWord.id = String(id)
        newDictionary.set(newWord.id, newWord)
        setMainDictionaryMap(newDictionary)

        textWordObjects[editedWordIndex].dictionaryWordId = newWord.id
        textWordObjects[editedWordIndex].translation = newWord.translation
        setTextWordObjects([...textWordObjects])

        console.log(newWord)
        onAddWordCanceled()

        saveDictionaryToLocalStorage()
    }

    const onAddWordCanceled = () => {
        setEditedWord(null)
        setEditedWordIndex(-1)
        setAddWordMode(false)
    }

    const makePosObject = (left, right) => {
        return { left, right }
    }

    const splitTextIntoPositions = async () => {
        let yes = window.confirm('This action removes all the mapped words and creates a new list of words. Shall we go?')
        if (!yes)
            return

        try {
            let data = {
                text: text.text ? text.text: ""
            }
            const response = await userRequest.post('/nlp/parse', data)
            let words = response.data.wordObjects

            // Store phrasal verbs
            // console.log("Received phrasal verbs:", response.data.phraseObjects)
            setPhraseObjects(response.data.phraseObjects || [])

            let posList = []
            let newTextWordObjects = []
            
            for (let i = 0; i < words.length; ++i) {
                let aWord = words[i]
                if (aWord.positions) {
                    let positions = makePosObject(aWord.positions.left, aWord.positions.right)
                    posList.push(positions)
                    
                    newTextWordObjects.push({ 
                        ...wordObjectTemplate, // set default values
                        ...aWord, // then set values from NLP
                        word: aWord.token,
                        translation: ''
                    })
                } else {
                    console.log("aWord has no positions")
                }
            }
    
            setPositionList(posList)
            setTextWordObjects([...newTextWordObjects])
        } catch (err) {
            
            if (err.response && err.response.status === 503) {
                alert("Failed to analyze text: " + err.response.data.message);
            } else {
                console.error("NLP analysis failed:", err)
                alert("Failed to analyze text: " + err.message);
            }

            return
        }
    }

    const onMainDictionaryChanged = (updatedDictionary) => {
        let newWordsArray = textWordObjects.map((wordObj) => {
            let dictWord = updatedDictionary.get(wordObj.dictionaryWordId)
            if (dictWord)
                wordObj.translation = dictWord.translation
            return wordObj
        })

        setMainDictionaryMap(updatedDictionary)
        setTextWordObjects(newWordsArray)

        saveDictionaryToLocalStorage()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {        
        fetchTextAPI()
        fetchTranslationAPI()
        
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!text || !mainDictionaryMap) return
        const timeoutId = setTimeout(() => {
            saveDictionaryToLocalStorage()
        }, 400)
        return () => clearTimeout(timeoutId)
    }, [textWordObjects, text, mainDictionaryMap])

    useEffect(() => {
        setVisibleWordRows(150)
    }, [textWordObjects.length])

    useEffect(() => {
        if (visibleWordRows >= textWordObjects.length) return
        const timeoutId = setTimeout(() => {
            setVisibleWordRows((prev) => Math.min(prev + 200, textWordObjects.length))
        }, 0)
        return () => clearTimeout(timeoutId)
    }, [visibleWordRows, textWordObjects.length])

    const moveLeftSidePos = (index, val) => {
        if (!text || !text.text) return;
        
        let newVal = positionList[index].left + val
        
        // TODO: check collision with positions in both sides

        if (newVal >= 0 && newVal < positionList[index].right) {
            // Create new arrays instead of mutating existing ones
            const newPositionList = [...positionList]
            const newTextWordObjects = [...textWordObjects]
            
            newPositionList[index] = { ...newPositionList[index], left: newVal }
            
            let positions = { ...newTextWordObjects[index].positions, left: newVal }
            const newWord = text.text.substring(positions.left, positions.right)
            
            newTextWordObjects[index] = { 
                ...newTextWordObjects[index], 
                positions: positions,
                word: newWord,
                lemma: newWord
            }
            
            setPositionList(newPositionList)
            setTextWordObjects(newTextWordObjects)
        }
    }

    const moveRightSidePos = (index, val) => {
        if (!text || !text.text) return;
        
        let newVal = positionList[index].right + val

        // TODO: check collision with positions in both sides
        
        if (positionList[index].left < newVal) {
            // Create new arrays instead of mutating existing ones
            const newPositionList = [...positionList]
            const newTextWordObjects = [...textWordObjects]
            
            newPositionList[index] = { ...newPositionList[index], right: newVal }

            let positions = { ...newTextWordObjects[index].positions, right: newVal }
            const newWord = text.text.substring(positions.left, positions.right)
            
            newTextWordObjects[index] = { 
                ...newTextWordObjects[index], 
                positions: positions,
                word: newWord,
                lemma: newWord
            }
            
            setPositionList(newPositionList)
            setTextWordObjects(newTextWordObjects)
        }
    }

    const addNewPos = (index, startOffset, rightOffset) => {
        if (!text || !text.text) return;
        
        // console.log(positionList[index])
        
        // make a new Postion
        let positions = { ...positionList[index] }
        positions.right = positions.left + rightOffset
        positions.left += startOffset
        
        // change the range of splitted word
        positionList[index].right = positions.left
        textWordObjects[index].positions.right = positions.left
        let newWord = text.text.substring(positions.left, positions.right)
        textWordObjects[index].word = newWord
        textWordObjects[index].lemma = newWord

        let newWordObj = { 
            ...wordObjectTemplate, 
            positions: positions, 
            word: newWord,
            lemma: newWord,
            // TODO: set tag and pos correctly. How? Manually?
            // Currently we hardcode it so that API server can validate this "required: true" fields in the data.
            tag: 'NN',
            pos: 'NOUN',
        }

        // insert new position
        if (index + 1 === positionList.length) {
            positionList.push(positions)
            textWordObjects.push(newWordObj)
        }
        else {
            positionList.splice(index + 1, 0, positions)
            textWordObjects.splice(index + 1, 0, newWordObj)
        }
        
        setPositionList([...positionList])
        setTextWordObjects([...textWordObjects])
    }

    const removePos = (index) => {
        if (index < positionList.length)
        {
            positionList.splice(index, 1)
            setPositionList([...positionList])

            textWordObjects.splice(index, 1)
            setTextWordObjects([...textWordObjects])
        }
    }

    const handleWordMappingChange = (wordIndex, dictObject) => {
        if (wordIndex >= textWordObjects.length) {
            alert('wordIndex is greater than available words. Returning...')
            return
        }

        console.log(wordIndex, dictObject)

        textWordObjects[wordIndex].dictionaryWordId = dictObject.id
        textWordObjects[wordIndex].translation = dictObject.translation
        setTextWordObjects([...textWordObjects])
    }


    const openWordInDictionary = (word) => {
        setWordToOpenInDictionary(word)
    }

    // AI Translation Functions
    const buildAITranslationPayload = () => {
        const dictionary_words = [];
        const dictionary_phrases = [];

        // Build dictionary words payload
        if (mainDictionaryMap) {
            for (let [id, dictWord] of mainDictionaryMap) {
                dictionary_words.push({
                    id: id,
                    word: dictWord.word,
                    translation: dictWord.translation || "",
                    pos: dictWord.pos
                });
            }
        }

        // Build dictionary phrases payload
        if (phrasesDictionary) {
            for (let [id, dictPhrase] of phrasesDictionary) {
                dictionary_phrases.push({
                    id: id,
                    phrase: dictPhrase.phrase,
                    translation: dictPhrase.translation || ""
                });
            }
        }

        return {
            text: text?.text || "",
            dictionary_words,
            dictionary_phrases
        };
    };

    const handleAITranslation = async (forceNewRequest = false) => {
        if (!text?.text || !mainDictionaryMap) {
            alert('Please make sure text is loaded and dictionary is created before using AI translation.');
            return;
        }

        // If we have a cached response and not forcing a new request, just open the dialog
        if (aiTranslationResponse && !forceNewRequest) {
            setAiTranslationDialogOpen(true);
            return;
        }

        setAiTranslationLoading(true);
        try {
            const payload = buildAITranslationPayload();
            console.log('AI Translation payload:', payload);

            const response = await userRequest.post('/nlp/translate', payload);
            console.log('AI Translation response:', response.data);

            setAiTranslationResponse(response.data);
            setAiTranslationDialogOpen(true);
        } catch (error) {
            console.error('AI Translation failed:', error);
            if (error.response?.status === 503) {
                alert('AI translation server is not available. Please try again later.');
            } else {
                alert('AI translation failed: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setAiTranslationLoading(false);
        }
    };

    const handleResendAITranslation = () => {
        handleAITranslation(true);
    };

    // Auto-translate function that calls the automated API
    const handleAutoTranslate = async () => {
        if (!text?._id) {
            alert('Text is not loaded. Please refresh and try again.');
            return;
        }

        const confirmed = window.confirm(
            'This will automatically:\n\n' +
            '1. Split text into words\n' +
            '2. Build dictionary\n' +
            '3. Map words and phrasal verbs\n' +
            '4. Get AI translations\n' +
            '5. Save everything to database\n\n' +
            'Any existing translation will be updated. Continue?'
        );

        if (!confirmed) return;

        setAutoTranslationLoading(true);
        try {
            console.log('Starting automated translation for text:', text._id);
            
            const response = await userRequest.post(`/autoTranslation/${text._id}`, {
                translationLanguage: translation?.translationLanguage || "63161a72739fbae23dc2f6e2",
                skipAI: false
            });

            console.log('Auto-translation response:', response.data);

            if (response.data.success) {
                // Refresh the data from the server
                await fetchTranslationAPI();
                
                const stats = response.data.statistics;
                alert(
                    `✅ Translation completed successfully!\n\n` +
                    `📊 Statistics:\n` +
                    `• Total words: ${stats.totalWords}\n` +
                    `• Mapped words: ${stats.mappedWords}\n` +
                    `• Total phrases: ${stats.totalPhrases}\n` +
                    `• Mapped phrases: ${stats.mappedPhrases}\n` +
                    `• Dictionary size: ${stats.dictionarySize}\n` +
                    `• Phrases dictionary size: ${stats.phrasesDictionarySize}\n` +
                    `• AI translation: ${stats.aiTranslationUsed ? 'Yes' : 'No'}\n\n` +
                    `The translation has been saved to the database.`
                );
            }
        } catch (error) {
            console.error('Auto-translation failed:', error);
            
            let errorMessage = 'Automated translation failed: ';
            if (error.response?.status === 503) {
                errorMessage += 'NLP server is not available. Please ensure the NLP server is running.';
            } else if (error.response?.data?.message) {
                errorMessage += error.response.data.message;
            } else {
                errorMessage += error.message || 'Unknown error occurred';
            }
            
            alert(errorMessage);
        } finally {
            setAutoTranslationLoading(false);
        }
    };

    const handleApplyAIChanges = async (changes, aiResponse) => {
        try {
            // Apply full translation immediately if changed
            if (changes.fullTranslation) {
                setWholeTextTranslation(changes.fullTranslation);
            }

            // Apply selected word changes
            if (changes.words.length > 0) {
                const updatedDictionary = new Map(mainDictionaryMap);
                
                for (let wordId of changes.words) {
                    const aiWord = aiResponse.updated_words.find(w => w.id === wordId);
                    if (aiWord && updatedDictionary.has(wordId)) {
                        const dictWord = updatedDictionary.get(wordId);
                        dictWord.translation = aiWord.translation;
                        updatedDictionary.set(wordId, dictWord);

                        // Update globalID if word exists in DictionaryWord collection
                        // This will be handled by the backend when saving
                    }
                }
                
                setMainDictionaryMap(updatedDictionary);
                
                // Update textWordObjects translations
                const updatedTextWords = textWordObjects.map(wordObj => {
                    if (changes.words.includes(wordObj.dictionaryWordId)) {
                        const dictWord = updatedDictionary.get(wordObj.dictionaryWordId);
                        return {
                            ...wordObj,
                            translation: dictWord?.translation || wordObj.translation
                        };
                    }
                    return wordObj;
                });
                setTextWordObjects(updatedTextWords);
            }

            // Apply selected phrase changes
            if (changes.phrases.length > 0 && phrasesDictionary) {
                const updatedPhrasesDictionary = new Map(phrasesDictionary);
                
                for (let phraseId of changes.phrases) {
                    const aiPhrase = aiResponse.updated_phrases.find(p => p.id === phraseId);
                    if (aiPhrase && updatedPhrasesDictionary.has(phraseId)) {
                        const dictPhrase = updatedPhrasesDictionary.get(phraseId);
                        dictPhrase.translation = aiPhrase.translation;
                        updatedPhrasesDictionary.set(phraseId, dictPhrase);
                    }
                }
                
                setPhrasesDictionary(updatedPhrasesDictionary);
            }

            // Add new phrases
            if (changes.newPhrases.length > 0) {
                const updatedPhrasesDictionary = phrasesDictionary ? new Map(phrasesDictionary) : new Map();
                const newPhraseObjects = [...phraseObjects];
                
                for (let index of changes.newPhrases) {
                    const newPhrase = aiResponse.new_phrases[index];
                    if (newPhrase) {
                        const phraseId = uuidv4();
                        
                        // Add to phrases dictionary
                        updatedPhrasesDictionary.set(phraseId, {
                            id: phraseId,
                            phrase: newPhrase.phrase,
                            translation: newPhrase.translation
                        });
                        
                        // Add to phrase objects (this should be done more carefully with proper position mapping)
                        // For now, we'll add it without position mapping
                        newPhraseObjects.push({
                            dictionaryPhraseId: phraseId,
                            phrase: newPhrase.phrase,
                            mapping_position: [] // This should be populated with actual positions
                        });
                    }
                }
                
                setPhrasesDictionary(updatedPhrasesDictionary);
                setPhraseObjects(newPhraseObjects);
            }

            alert(`Successfully applied AI translation changes:\n• ${changes.words.length} words updated\n• ${changes.phrases.length} phrases updated\n• ${changes.newPhrases.length} new phrases added`);
            
        } catch (error) {
            console.error('Error applying AI changes:', error);
            alert('Failed to apply some changes: ' + error.message);
        }
    };



    const StatusChip = ({ done }) => (
        <Chip 
            label={done ? '✓' : '○'} 
            size="small" 
            color={done ? 'success' : 'default'}
            sx={{ ml: 0.5, minWidth: 24 }}
        />
    );

    const isRenderingWordRows = textWordObjects.length > 0 && visibleWordRows < textWordObjects.length;
    const isOverlayLoading = loadingText || loadingTranslation || autoTranslationLoading || isRenderingWordRows;
    const overlayMessage = autoTranslationLoading
        ? "Auto-translating... please wait"
        : isRenderingWordRows
            ? `Preparing word-by-word editor... (${Math.min(visibleWordRows, textWordObjects.length)}/${textWordObjects.length})`
            : "Loading text and translation...";

    return (
        <PageContainer maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" component="h1" fontWeight="bold">
                    Edit Text / Translation
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="How to use the translation editor">
                        <IconButton onClick={() => setUserGuideOpen(true)} color="info" size="medium">
                            <HelpIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {isOverlayLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1400,
                        bgcolor: "rgba(20, 20, 28, 0.16)",
                        pointerEvents: "none"
                    }}
                >
                    <Box
                        sx={{
                            position: "sticky",
                            top: 16,
                            display: "flex",
                            justifyContent: "center",
                            px: 2
                        }}
                    >
                        <Paper
                            elevation={6}
                            sx={{
                                width: "min(560px, 100%)",
                                p: 2,
                                borderRadius: 2,
                                border: "2px solid",
                                borderColor: "warning.main",
                                pointerEvents: "none"
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                                <CircularProgress size={22} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {overlayMessage}
                                </Typography>
                            </Box>
                            <LinearProgress />
                        </Paper>
                    </Box>
                </Box>
            )}

            {!loadingTranslation && translationNotFound && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No translation was found for this text yet. You can create one now.
                </Alert>
            )}

            {error && !translationNotFound && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* 1. Main Text - Source */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Original Text</Typography>
                <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1, 
                    fontSize: '1.05em', 
                    lineHeight: 1.6,
                    minHeight: 48
                }}>
                    {loadingText ? 'Loading text...' : (text?.text || 'No text found')}
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TranslationViewerDialog 
                        text={text}
                        textWordObjects={textWordObjects}
                        dictionary={mainDictionaryMap}
                        phrasalVerbs={phraseObjects}
                        phrasesDictionary={phrasesDictionary}
                    />
                </Box>
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* 2. Whole Text Translation - Resizable */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>2. Whole Text Translation</Typography>
                <TranslationCreator
                    textTranslation={wholeTextTranslation}
                    onWholeTextTranslation={setWholeTextTranslation}
                    originalText={text?.text}
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 3. Manual Steps - Word-level editing */}
            <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Use the steps below to manually control translation and dictionary mapping.
                </Alert>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, alignItems: 'start' }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="medium">Editor</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                                variant="outlined" 
                                size="small"
                                startIcon={<DictionaryIcon />}
                                endIcon={<StatusChip done={!!mainDictionaryMap} />}
                                disabled={!mainDictionaryMap}
                                title="Opens words dictionary editor"
                                onClick={() => setWordToOpenInDictionary({})}
                            >
                                Words dictionary
                            </Button>
                            {phrasesDictionary && (
                                <TranslationPhraseEditor
                                    phraseObjects={phraseObjects}
                                    phrasesDictionary={phrasesDictionary}
                                    onPhrasesDictionaryChange={setPhrasesDictionary}
                                    onPhraseObjectsChange={setPhraseObjects}
                                    text={text?.text}
                                    wordObjects={textWordObjects}
                                />
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {autoTranslationLoading && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Automated Translation in Progress...</Typography>
                    <Typography variant="body2">Please wait while the system splits text, builds dictionaries, maps words, gets AI translations, and saves. This may take 30-60 seconds.</Typography>
                </Alert>
            )}

            {/* 5. Word-level editing */}
            {textWordObjects.length > 0 && text && (
                <Paper elevation={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight="medium">5. Word-by-Word Editing</Typography>
                        <Typography variant="caption" color="text.secondary">Edit boundaries, translations, and map to dictionary</Typography>
                        {visibleWordRows < textWordObjects.length && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Rendering rows {Math.min(visibleWordRows, textWordObjects.length)} / {textWordObjects.length}
                                </Typography>
                                <LinearProgress sx={{ mt: 0.5 }} />
                            </Box>
                        )}
                    </Box>
                    {textWordObjects.slice(0, visibleWordRows).map((word, index) => (
                        <OneWordEditor
                            key={`${index}-${word.positions.left}-${word.positions.right}-${word.word}`}
                            index={index}
                            precedingText={index === 0 ? text.text.slice(0, word.positions.left) : text.text.slice(textWordObjects[index - 1].positions.right, word.positions.left)}
                            word={textWordObjects[index].word} 
                            linkTitle={mainDictionaryMap && mainDictionaryMap.get(textWordObjects[index]?.dictionaryWordId)?.linkTitle}
                            succeedingText={index === textWordObjects.length - 1 ? text.text.slice(word.positions.right) : ''}
                            translation={textWordObjects[index].dictionaryWordId?.length > 0 ? (textWordObjects[index].translation || '') : ''} 
                            moveLeftFunc={moveLeftSidePos}
                            moveRightFunc={moveRightSidePos}
                            splitFunc={addNewPos}
                            removeFunc={removePos}
                            wordDictionary={mainDictionaryMap}
                            onChangeWordDescription={handleWordMappingChange}
                            addNewWordFunc={onAddWordButtonClicked}
                            dictionaryOpener={() => openWordInDictionary(word)}
                        />
                    ))}
                </Paper>
            )}

            { addWordMode && 
                <WordAddForm 
                    word={editedWord} 
                    isDialog={true} 
                    onAcceptFunc={onAddWord}
                    onCancelFunc={onAddWordCanceled}>
                </WordAddForm>}

            {mainDictionaryMap && 
                wordToOpenInDictionary &&
                <DictionaryEditorDialog 
                    mainDictionary={mainDictionaryMap} 
                    dictionarySetter={onMainDictionaryChanged}
                    wordToOpen={wordToOpenInDictionary}
                    onCloseFunction={() => setWordToOpenInDictionary(null)}>
                </DictionaryEditorDialog>}

            <AITranslationDialog 
                open={aiTranslationDialogOpen}
                onClose={() => setAiTranslationDialogOpen(false)}
                aiResponse={aiTranslationResponse}
                onApplyChanges={handleApplyAIChanges}
                currentFullTranslation={wholeTextTranslation}
                onResendRequest={handleResendAITranslation}
                isLoading={aiTranslationLoading}
            />

            <Box sx={{
                position: 'fixed',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                zIndex: 1000
            }}>
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={onSaveButtonClicked}
                >
                    Save
                </Button>
                <Button 
                    variant="outlined" 
                    startIcon={<CancelIcon />}
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Box>

            <Box sx={{ height: 60 }} />

            <TranslationEditorGuide 
                open={userGuideOpen}
                onClose={() => setUserGuideOpen(false)}
            />

        </PageContainer>
    );
}

export default TranslationEditor;